import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DailyIframe from "@daily-co/daily-js";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Video, ArrowLeft, AlertCircle, Stethoscope } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Doctor consultation video call page. Auto-fetches the user-specific join token and
 * embeds the Daily Prebuilt iframe full-screen.
 *
 * Lifecycle:
 *   - GET /api/video/appointments/:id/join → { room_url, token, role, ... }
 *   - DailyIframe.createFrame(...).join({ url, token })
 *   - 'left-meeting' event → navigate back
 */
export default function VideoCall() {
    const { id } = useParams();
    const nav = useNavigate();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [ended, setEnded] = useState(false);
    const frameRef = useRef(null);
    const containerRef = useRef(null);

    // 1) Fetch room url + token
    useEffect(() => {
        api.get(`/video/appointments/${id}/join`)
            .then(r => setInfo(r.data))
            .catch(e => setError(e.response?.data?.detail || "Couldn't join the call"));
    }, [id]);

    // 2) Init Daily Prebuilt iframe
    useEffect(() => {
        if (!info || !containerRef.current || frameRef.current) return;
        const frame = DailyIframe.createFrame(containerRef.current, {
            iframeStyle: { width: "100%", height: "100%", border: "0" },
            showLeaveButton: true,
            showFullscreenButton: true,
            theme: { colors: { accent: "#0369a1", accentText: "#ffffff" } },
        });
        frameRef.current = frame;
        frame.on("joined-meeting", () => toast.success("Connected"));
        frame.on("left-meeting", () => setEnded(true));
        frame.on("error", (e) => {
            console.error("Daily error", e);
            toast.error(e?.errorMsg || "Call error");
        });
        frame.join({ url: info.room_url, token: info.token }).catch((e) => {
            console.error("join failed", e);
            setError(`Failed to join: ${e?.errorMsg || e?.message || "unknown error"}`);
        });
        return () => {
            try { frame.destroy(); } catch { /* noop */ }
            frameRef.current = null;
        };
    }, [info]);

    if (error) {
        return (
            <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
                <div className="max-w-md bg-white border border-red-200 p-8 text-center" data-testid="video-error">
                    <AlertCircle className="w-10 h-10 mx-auto text-red-600 mb-3" />
                    <h2 className="font-serif text-xl text-stone-900 mb-2">We couldn't start the call</h2>
                    <p className="text-sm text-red-800 mb-4">{error}</p>
                    <Button onClick={() => nav(-1)} className="rounded-none bg-stone-800 hover:bg-stone-900 text-stone-50"><ArrowLeft className="w-4 h-4 mr-1" />Go back</Button>
                </div>
            </div>
        );
    }
    if (!info) {
        return <div className="min-h-screen flex items-center justify-center text-sm text-stone-500" data-testid="video-loading">Connecting to consultation…</div>;
    }

    return (
        <div className="fixed inset-0 bg-stone-950 z-50 flex flex-col" data-testid="video-page">
            <Toaster position="top-center" />
            {/* Header */}
            <div className="bg-stone-900 text-stone-50 px-4 py-3 flex items-center gap-3 print:hidden">
                <Video className="w-5 h-5 text-sky-400" />
                <div className="flex-1">
                    <div className="font-serif text-lg leading-tight">{info.role === "doctor" ? `Consultation with ${info.patient_name}` : `Dr. ${info.doctor_name}`}</div>
                    <div className="text-[11px] text-stone-400">Appointment · {info.scheduled_at} · you joined as <strong className="text-sky-300">{info.role}</strong></div>
                </div>
                <Button onClick={() => nav(-1)} variant="ghost" size="sm" className="text-stone-50 hover:bg-stone-800 rounded-none" data-testid="video-leave-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" />Leave
                </Button>
            </div>
            {/* Daily iframe holder */}
            <div className="flex-1 relative" ref={containerRef} data-testid="video-frame" />
            {/* Ended overlay */}
            {ended && (
                <div className="absolute inset-0 bg-stone-950/95 flex items-center justify-center z-10">
                    <div className="bg-white border border-stone-200 p-8 max-w-sm text-center">
                        <Stethoscope className="w-10 h-10 mx-auto text-sky-700 mb-2" />
                        <h2 className="font-serif text-xl text-stone-900 mb-1">Consultation ended</h2>
                        <p className="text-sm text-stone-600 mb-4">Thanks for using Hahnemann Online Consultation. {info.role === "patient" && "Take care!"}</p>
                        <div className="flex flex-col gap-2">
                            <Button onClick={() => nav("/my-appointments")} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Back to my appointments</Button>
                            {info.role === "patient" && (
                                <Link to={`/doctors`} className="text-xs text-stone-500 hover:underline">Rate the doctor →</Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
